/**
 * Resource Plan arithmetic — pure, integer-only, no I/O.
 *
 * Rows are Plan Lines, columns are months, cells are hours, and a rate turns hours into
 * dollars. This module is that turn, at every grain the UI shows: cell, line, month,
 * Workstream, plan.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * UNITS — the same three integer scales as src/lib/rate-card.ts, plus one
 * ──────────────────────────────────────────────────────────────────────────────
 * Money is `Micros` (dollars × 1e6) and percentages are `Bp` (percent × 100), exactly as
 * M1 defines them, so `formatMicrosAsMoney`, `formatBpAsPercent`, `marginBp` and
 * `marginStatus` are reused verbatim rather than reimplemented. The addition is
 * `HoursCenti` — hours × 100, because `plan_line_month.hours` is `numeric(8,2)`.
 *
 * A cell's revenue falls out of the two:
 *
 *   rateCents (dollars × 1e2) × hoursCenti (hours × 1e2) × 100 = dollars × 1e6 = Micros
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY MARGIN IS COMPUTED AT CENTS SCALE AND NOTHING ELSE IS
 * ──────────────────────────────────────────────────────────────────────────────
 * `marginBp(revenue, cost)` multiplies by 10,000. At Micros that overflows 2^53 on a
 * large plan — 40 lines × 36 months × 160 h × $300 is ≈ $69M, and 6.9e13 × 1e4 × 2 is
 * 1.4e18. So the margin pair is reduced to cents first, which is lossless whenever hours
 * are whole (every plan in the acceptance corpus) and at worst half a cent on a total of
 * millions otherwise — a figure that cannot move a percentage printed to 2dp.
 *
 * This is NOT the rounding trap M1 documents. That trap is pre-rounding the BLENDED COST
 * — a per-hour average whose sub-cent tail decides 64.84% vs 64.85%. Here the pair being
 * reduced is a total amount of money, which is denominated in cents in the real world.
 * Blended Rate is still derived from the unreduced Micros total, for exactly that reason.
 *
 * Every other figure rounds once, at the presentation boundary, via M1's formatters.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-6.4 — THE UNWEIGHTED AVERAGE IS BANNED
 * ──────────────────────────────────────────────────────────────────────────────
 * Blended Rate is Total Revenue ÷ Total Hours. It is never the mean of the rate column.
 * The TBOS workbook prints `=AVERAGE(F7:F25)` = $186.05 against a true blended rate of
 * $175.67; quoting the printed figure invents $250,711 across 24,140 hours. There is no
 * function in this module that averages rates, and there must never be one.
 */

import { feeOfRecordMinor, monthRevenueBasis, spreadByWeights } from "@/lib/fee-spread";
import {
  type Bp,
  FULL_PERCENT_BP,
  type MarginPolicy,
  type MarginStatus,
  type Micros,
  marginBp,
  marginStatus,
  parseMinor,
} from "./rate-card";

/** Hours at scale 2 — hours × 100. `plan_line_month.hours` is `numeric(8,2)`. */
export type HoursCenti = number;

/** cents × centi-hours → Micros. */
const MICROS_PER_CENTI_CENT = 100;
/** Micros per cent (1e4): the reduction applied before a margin division. */
const MICROS_PER_CENT = 10_000;
/** Ramp factors are `numeric(4,3)` — factor × 1000. 1.000 → 1000. */
const RAMP_SCALE = 1_000;
/** No ramp row means full ramp (RP-4.3's default of 1.0). */
const FULL_RAMP_MILLI = RAMP_SCALE;

/**
 * Guard every accumulation. A silently-wrong total on a $4M plan is far worse than a
 * throw, and 2^53 is close enough to be reachable — see the header.
 */
function assertSafe(n: number, what: string): void {
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(`${what} exceeded exact integer range: ${n}`);
  }
}

/**
 * Exact integer division with half-up rounding, symmetric about zero.
 *
 * Exported so every consumer that derives an hours figure rounds the SAME way — a second
 * `Math.round` elsewhere is the drift this module's header exists to prevent.
 */
export function divideRoundHalfUp(numerator: number, denominator: number): number {
  assertSafe(numerator, "division numerator");
  if (denominator === 0) throw new RangeError("division by zero");
  const sign = numerator < 0 !== denominator < 0 ? -1 : 1;
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  return sign * Math.floor((2 * n + d) / (2 * d));
}

/** Parse an hours string (`"160.00"`, `"37.5"`) into integer hundredths. */
export function parseHoursCenti(value: string): HoursCenti {
  return parseMinor(value, 2);
}

/**
 * One line's cells, expanded and ramped — the single definition of "what hours does this
 * line actually carry, in which months".
 *
 * Two transformations, in this order, and both matter:
 *
 *   1. SteadyState expansion (RP-4.4) — the line's one run-rate figure becomes a cell in
 *      every month of the plan. In that mode `plan_line_month` holds nothing.
 *   2. Ramp (RP-4.3) — each cell is multiplied by its month's factor and rounded to
 *      centi-hours once, so the cell figure the grid shows and the figure the totals are
 *      built from are the same number.
 *
 * Exported because `computePlanMetrics` is not the only consumer: RP-2.4's "planned hours
 * per Position per month" rolls the same cells up by Planned Pod Position. A second
 * implementation of either transformation would drift, and the Planned Pod panel would
 * quietly disagree with the grid footer it sits under.
 */
export function expandLineCells(input: {
  line: Pick<PlanLineInput, "months" | "steadyStateHours">;
  months: readonly string[];
  entryMode?: "MonthByMonth" | "SteadyState";
  /** month → ramp milli (factor × 1000). Absent means 1.0. */
  rampMilliByMonth?: ReadonlyMap<string, number>;
}): Array<{ month: string; enteredCenti: HoursCenti; hoursCenti: HoursCenti }> {
  const steadyState = input.entryMode === "SteadyState";
  const raw: readonly PlanLineMonthInput[] = steadyState
    ? (input.line.steadyStateHours ?? null) === null
      ? []
      : input.months.map((month) => ({ month, hours: input.line.steadyStateHours ?? "0" }))
    : input.line.months;

  return raw.map((cell) => {
    const enteredCenti = parseHoursCenti(cell.hours);
    return {
      month: cell.month,
      enteredCenti,
      hoursCenti: rampHoursCenti(
        enteredCenti,
        input.rampMilliByMonth?.get(cell.month) ?? FULL_RAMP_MILLI,
      ),
    };
  });
}

/**
 * One cell's EFFECTIVE hours (RP-4.3): entered × ramp, rounded to centi-hours once.
 * Exported so the grid's draft footer ramps a cell exactly as the server does.
 */
export function rampHoursCenti(enteredCenti: HoursCenti, rampMilli: number): HoursCenti {
  return rampMilli === FULL_RAMP_MILLI
    ? enteredCenti
    : divideRoundHalfUp(enteredCenti * rampMilli, RAMP_SCALE);
}

/** Parse a `numeric(4,3)` ramp map into the milli form `expandLineCells` takes. */
export function parseRampMap(
  rampByMonth: ReadonlyMap<string, string> | undefined,
): Map<string, number> {
  const out = new Map<string, number>();
  if (rampByMonth === undefined) return out;
  for (const [month, factor] of rampByMonth) out.set(month, parseMinor(factor, 3));
  return out;
}

/** Ramp milli → a `numeric(4,3)` string. `1000` → `"1.000"`, `500` → `"0.500"`. */
export function formatRampMilli(rampMilli: number): string {
  const whole = Math.trunc(rampMilli / RAMP_SCALE);
  const frac = rampMilli % RAMP_SCALE;
  return `${whole}.${String(frac).padStart(3, "0")}`;
}

/** Micros → an hours string with 2dp, for display. */
export function formatHoursCenti(hoursCenti: HoursCenti): string {
  const whole = Math.trunc(Math.abs(hoursCenti) / 100);
  const frac = Math.abs(hoursCenti) % 100;
  const sign = hoursCenti < 0 ? "-" : "";
  return `${sign}${whole}.${String(frac).padStart(2, "0")}`;
}

// ── Inputs ────────────────────────────────────────────────────────────────────

/** One cell. `hours` is the `numeric(8,2)` string Drizzle returns. */
export interface PlanLineMonthInput {
  /** `YYYY-MM-01`. */
  month: string;
  hours: string;
}

/**
 * One Plan Line, joined to its Labor Category's cost.
 *
 * `avgInternalCost` is the ONLY cost input, and it is present regardless of the reader's
 * role — the stripping happens in the API shaper, not here. A pure module that refused to
 * compute cost could not tell an [ACCT] user whether a line is below cost, which RP-6.2
 * requires it to do (as a boolean, never a magnitude).
 */
export interface PlanLineInput {
  id: string;
  workstreamId: string | null;
  laborCategoryId: string;
  /** `numeric(10,2)` as a string. */
  billRate: string;
  /** `numeric(10,2)` as a string, from `labor_category.avg_internal_cost`. */
  avgInternalCost: string;
  /**
   * The stored cells. IGNORED when the plan is in SteadyState mode — in that mode
   * `plan_line_month` holds nothing and the months come from `steadyStateHours`.
   */
  months: readonly PlanLineMonthInput[];
  /**
   * The run-rate: hours per month, expanded across every month of the plan (RP-4.4).
   * Read only in SteadyState mode; NULL there means no hours yet.
   */
  steadyStateHours?: string | null;
  /**
   * The line's Suggested Resource's Internal Rate per month (2026-10-14,
   * docs/specs/resource-internal-rates.md): `YYYY-MM-01` → `numeric(10,2)` string. A month
   * present here REPLACES `avgInternalCost` for that month's cells; a month absent keeps
   * the Labor Category cost. Omitted or empty → the line costs exactly as it always did.
   */
  costByMonth?: ReadonlyMap<string, string>;
  /**
   * A SPLIT line's cost portions per month (RP-24 Line Shares): each Share's entered hours
   * that month and its cost rate in cents (an Internal Rate, or null for the Labor Category
   * cost). A month present here is costed from its portions — hour-weighted, exactly, with
   * any hours the portions leave uncovered at the Labor Category cost — and `costByMonth` is
   * not consulted for it. A split line has no Suggested Resource, so the two never both
   * apply. Omitted on an unsplit line.
   */
  costPortionsByMonth?: ReadonlyMap<
    string,
    ReadonlyArray<{ enteredCenti: HoursCenti; costCents: number | null }>
  >;
}

/** Where a line's cost came from. `mixed`: some months Resource-priced, some not. */
export type LineCostSource = "labor_category" | "resource" | "mixed";

export interface PlanMetricsInput {
  lines: readonly PlanLineInput[];
  /** Every month column the grid shows, `YYYY-MM-01`, in order. */
  months: readonly string[];
  /**
   * How hours are entered (RP-4.4).
   *
   * `SteadyState` expands each line's single `steadyStateHours` figure across `months`,
   * which is what makes a Monthly run-rate one number instead of 36 identical ones. Every
   * total, margin and per-month column is computed from the expansion, so the two modes
   * are indistinguishable downstream — only the INPUT differs.
   *
   * Defaults to MonthByMonth when omitted, so every existing caller is unaffected.
   */
  entryMode?: "MonthByMonth" | "SteadyState";
  /**
   * Ramp factor per month (RP-4.3), keyed `YYYY-MM-01`, values the raw `numeric(4,3)`
   * strings Drizzle returns from `plan_month_ramp`.
   *
   * ABSENCE MEANS 1.0. Only months the planner de-rated have rows, so the common plan
   * passes an empty map or omits this entirely — and every M2 caller, which omits it,
   * computes exactly what it computed before.
   *
   * RP-4.3: "Effective hours = entered hours × ramp factor; effective hours drive all
   * totals. The grid shows entered hours with the factor visible." Both figures are
   * therefore reported at every grain below: `hoursCenti` is EFFECTIVE and drives revenue,
   * cost, margin and FTE; `enteredHoursCenti` is what was typed, for the grid to show.
   */
  rampByMonth?: ReadonlyMap<string, string>;
  /** `resource_plan.contracted_fee` — `numeric(14,2)` as a string, or null on T&M. */
  contractedFee: string | null;
  /**
   * `resource_plan.contracted_fee_basis`. A **Monthly** fee is per month, so the margin of
   * record measures `fee × months` against the window's cost (RP-D16); a Total fee (and an
   * absent basis, for callers that predate it) is already the whole.
   */
  contractedFeeBasis?: string | null;
  /**
   * `resource_plan.pricing_model`. Only used to say what a MONTH's revenue means — see
   * src/lib/fee-spread.ts. Absent reads as T&M, whose months are always hours × rate.
   */
  pricingModel?: string;
  /** `resource_plan.discount_percent` — applied to revenue of record. */
  discountPercent: string | null;
  /** `resource_plan.discount_amount` — applied to revenue of record. */
  discountAmount: string | null;
  /**
   * Hours per FTE-month in CENTI — hours × 100, an exact integer. Display reference only.
   *
   * Centi, not hours, because the only consumer immediately multiplies by 100 again: a
   * `parseMinor(x,2)/100` round trip is exact for "160.00" and yields
   * 17332.999999999996 for "173.33", which then flows into divideRoundHalfUp as a
   * non-integer denominator.
   */
  hoursPerFteMonthCenti: number;
  /** The resolved basis's floor and amber band, from resolveMarginPolicy(). */
  marginPolicy: MarginPolicy;
}

// ── Outputs ───────────────────────────────────────────────────────────────────

export interface LineMetrics {
  lineId: string;
  /** EFFECTIVE hours — entered × ramp. Drives revenue, cost and margin (RP-4.3). */
  hoursCenti: HoursCenti;
  /** What was typed into the cells, before ramp. Equal to `hoursCenti` on an unramped plan. */
  enteredHoursCenti: HoursCenti;
  revenueMicros: Micros;
  /** Cost. Stripped for anyone without `viewPlanCost` — see shapePlanForActor(). */
  costMicros: Micros;
  /** Revenue − cost. Stripped alongside cost. */
  netMicros: Micros;
  /**
   * Indicative margin. Below plan grain only Indicative is meaningful: a Contracted Fee
   * has no per-line allocation and none is invented (RP-7.1).
   */
  marginBp: Bp | null;
  /**
   * RP-6.2: the applied rate is below the line's cost rate — in ANY month, when a
   * Resource's Internal Rate varies by month. A BOOLEAN, never the magnitude —
   * non-negotiable 3. Safe to send to [ACCT]; the delta is not.
   */
  belowCost: boolean;
  /** Labor Category cost, a Resource's Internal Rate, or both across months. */
  costSource: LineCostSource;
  /**
   * The line's cost rate in cents. With one rate across the line, that rate. When a
   * Resource's rate changes mid-plan, line cost ÷ line hours (hour-WEIGHTED, never an
   * average of rates); on a zero-hour line, the highest rate in play.
   */
  costRateCents: number;
}

export interface MonthMetrics {
  month: string;
  /** EFFECTIVE hours — entered × this month's ramp factor. */
  hoursCenti: HoursCenti;
  enteredHoursCenti: HoursCenti;
  /** This month's ramp factor as a `numeric(4,3)` string. "1.000" where none is set. */
  rampFactor: string;
  revenueMicros: Micros;
  costMicros: Micros;
  /**
   * What this month earns on the margin of record (RP-4.1 / RP-D16) — hours × rate on
   * T&M, the fee itself on a Monthly-basis fee, and the Fee Spread (fee × this month's
   * effective hours ÷ the plan's) on Fixed Price × Total. Null only on a Fixed Price ×
   * Total plan with no hours, which has nothing to spread the fee on.
   * See src/lib/fee-spread.ts. Plan discounts are not allocated to months.
   */
  revenueOfRecordMicros: Micros | null;
  /** Margin on `revenueOfRecordMicros`. Null with no revenue. Cost-derived but a %. */
  marginBp: Bp | null;
  /** hours ÷ hoursPerFteMonth, at scale 2. A REFERENCE FIGURE — never persisted (§4.5). */
  fteCenti: number;
}

export interface WorkstreamMetrics {
  /** null is the plan-level group — lines outside any Workstream (RP-3.1). */
  workstreamId: string | null;
  hoursCenti: HoursCenti;
  enteredHoursCenti: HoursCenti;
  revenueMicros: Micros;
  costMicros: Micros;
  netMicros: Micros;
  marginBp: Bp | null;
  blendedRateMicros: Micros | null;
  lineCount: number;
}

export interface PlanMetrics {
  /** EFFECTIVE hours — the plan's real size once ramp is applied (RP-4.3). */
  hoursCenti: HoursCenti;
  /** Σ of what was typed, before ramp. Equal to `hoursCenti` on an unramped plan. */
  enteredHoursCenti: HoursCenti;
  /** Whether any month carries a factor below 1.0 — drives the "ramped" chip. */
  ramped: boolean;
  /** Σ (line hours × applied bill rate). Always derived, always present (§4.4). */
  indicativeRevenueMicros: Micros;
  costMicros: Micros;
  netMicros: Micros;

  /** Total Revenue ÷ Total Hours. Weighted, always. Null on a plan with no hours. */
  blendedRateMicros: Micros | null;

  /** The Contracted Fee as Micros, as entered (per month on a Monthly basis), or null on T&M. */
  contractedFeeMicros: Micros | null;
  /** Contracted Fee of record over the window: `fee × months` on a Monthly basis (RP-D16). */
  contractedFeeOfRecordMicros: Micros | null;
  /** Fee of record − Indicative Revenue, over the window. Signed. Null with no fee. */
  feeVarianceMicros: Micros | null;
  /** Fee Variance as a percentage of Indicative Revenue. Signed. */
  feeVarianceBp: Bp | null;

  /** Discount in dollars, however it was expressed. Zero when none is set. */
  discountMicros: Micros;

  /** "What would this work be worth at our own rates?" — diagnostic, never a gate. */
  indicativeMarginBp: Bp | null;
  /** "What do we actually make?" — null with no fee. */
  contractedMarginBp: Bp | null;
  /** Contracted where a fee exists, Indicative otherwise (§4.4). Drives the floor. */
  marginOfRecordBp: Bp | null;
  /** Which of the two `marginOfRecordBp` is, so the UI can label it honestly. */
  marginOfRecordKind: "contracted" | "indicative" | "none";
  /** green / amber / red against the resolved basis's floor and band (RP-7.1). */
  marginStatus: MarginStatus | null;

  byMonth: MonthMetrics[];
  byWorkstream: WorkstreamMetrics[];
  byLine: LineMetrics[];

  /** RP-6.2: any line priced below its Labor Category's cost. Boolean, never magnitudes. */
  hasBelowCostLine: boolean;
  belowCostLineCount: number;
}

// ── The Labor Category baseline (RP-7.5) ──────────────────────────────────────

/**
 * The plan's margin of record as it would be if every hour were costed at its Labor
 * Category's `avgInternalCost` — the figure the plan had before anyone was named on it
 * (RP-7.5, 2026-10-17). Set beside `metrics.marginOfRecordBp`, the gap is what the named
 * Resources' Internal Rates did to the deal.
 *
 * Null when no line is priced at an Internal Rate: the baseline would equal the plan's
 * own margin, so there is nothing to compare. Same fee, discount and ramp as the plan,
 * so the only thing that differs is cost.
 */
export function laborCategoryBaselineMarginBp(
  input: PlanMetricsInput,
  metrics: PlanMetrics,
): Bp | null {
  if (metrics.byLine.every((l) => l.costSource === "labor_category")) return null;
  // No Resource rate and no Line Share portions: every month at the Labor Category cost —
  // see `costByMonth` and `costPortionsByMonth`.
  const baseline = computePlanMetrics({
    ...input,
    lines: input.lines.map((l) => ({
      ...l,
      costByMonth: new Map(),
      costPortionsByMonth: undefined,
    })),
  });
  return baseline.marginOfRecordBp;
}

// ── The computation ───────────────────────────────────────────────────────────

/**
 * Every derived figure on a plan, at every grain, from the Plan Lines and the fee.
 *
 * One pass over the cells. At the 40 × 36 bar that is 1,440 iterations of integer
 * arithmetic — cheap enough to run on every keystroke behind a `useDeferredValue`, which
 * is exactly what RP-7.3 ("the number on screen is never stale") asks for.
 */
export function computePlanMetrics(input: PlanMetricsInput): PlanMetrics {
  const monthIndex = new Map<string, number>();
  for (const [i, m] of input.months.entries()) monthIndex.set(m, i);

  // Ramp is resolved ONCE per month here, not per cell: 1,440 cells over 36 months would
  // otherwise re-parse the same 36 numeric strings forty times over.
  const rampMilliByMonth = parseRampMap(input.rampByMonth);
  let ramped = false;
  for (const milli of rampMilliByMonth.values()) {
    if (milli !== FULL_RAMP_MILLI) ramped = true;
  }

  const byMonthAccum = input.months.map((month) => ({
    month,
    hoursCenti: 0,
    enteredHoursCenti: 0,
    rampMilli: rampMilliByMonth.get(month) ?? FULL_RAMP_MILLI,
    revenueMicros: 0,
    costMicros: 0,
  }));

  const wsAccum = new Map<
    string | null,
    {
      hoursCenti: number;
      enteredHoursCenti: number;
      revenueMicros: number;
      costMicros: number;
      lineCount: number;
    }
  >();

  const byLine: LineMetrics[] = [];
  let planHoursCenti = 0;
  let planEnteredHoursCenti = 0;
  let planRevenueMicros = 0;
  let planCostMicros = 0;
  let belowCostLineCount = 0;

  for (const line of input.lines) {
    const rateCents = parseMinor(line.billRate, 2);
    const costCents = parseMinor(line.avgInternalCost, 2);
    // Resource Internal Rate per month, parsed once per line, not once per cell.
    const resourceCostCents = new Map<string, number>();
    for (const [month, cost] of line.costByMonth ?? []) {
      resourceCostCents.set(month, parseMinor(cost, 2));
    }
    // A split line (RP-24): per month, the highest cost among the portions that work that
    // month — and the Labor Category cost too when the portions leave hours uncovered. It
    // is the below-cost bar ("the HIGHEST cost in a month the line actually works").
    const portionsByMonth = line.costPortionsByMonth;
    const portionMaxCents = new Map<string, number>();
    const portionRated = new Set<string>();
    for (const [month, portions] of portionsByMonth ?? []) {
      let max: number | null = null;
      for (const p of portions) {
        if (p.costCents !== null) portionRated.add(month);
        const c = p.costCents ?? costCents;
        max = max === null ? c : Math.max(max, c);
      }
      if (max !== null) portionMaxCents.set(month, max);
    }
    const costCentsFor = (month: string): number =>
      portionMaxCents.get(month) ?? resourceCostCents.get(month) ?? costCents;
    // The below-cost bar for a worked cell. On a split month whose portions leave hours
    // uncovered, those hours are worked at the Labor Category cost, so it joins the bar.
    const workedBarCents = (month: string, enteredCenti: number): number => {
      const portions = portionsByMonth?.get(month);
      const bar = costCentsFor(month);
      if (portions === undefined || portions.length === 0) return bar;
      const covered = portions.reduce((sum, p) => sum + p.enteredCenti, 0);
      return covered < enteredCenti ? Math.max(bar, costCents) : bar;
    };
    // Exact cost of one cell. Unsplit months: rate × effective hours, as always. Split
    // months: Σ(portion hours × portion rate), with uncovered hours at the Labor Category
    // cost, scaled by effective ÷ entered so a ramp de-rates every portion alike. When the
    // portions exceed the line (an over-allocated month), the weights are normalised over
    // the portions themselves so cost still follows the line's own hours.
    const cellCostMicros = (month: string, enteredCenti: number, hoursCenti: number): number => {
      const portions = portionsByMonth?.get(month);
      if (portions === undefined || portions.length === 0 || enteredCenti <= 0) {
        return costCentsFor(month) * hoursCenti * MICROS_PER_CENTI_CENT;
      }
      // Split the cell's EFFECTIVE hours across the portions (plus any uncovered remainder
      // at the Labor Category cost) in proportion to their entered hours, then price each
      // part at its own rate. Dividing the hours first keeps every product the same size as
      // the unsplit path's — multiplying hours by hours before dividing would leave 2^53 at
      // a few hundred hours. The last part takes the rounding remainder, so the parts always
      // add up to the cell's hours exactly.
      const parts: Array<{ enteredCenti: number; costCents: number }> = portions.map((p) => ({
        enteredCenti: p.enteredCenti,
        costCents: p.costCents ?? costCents,
      }));
      const covered = portions.reduce((sum, p) => sum + p.enteredCenti, 0);
      if (covered < enteredCenti) {
        parts.push({ enteredCenti: enteredCenti - covered, costCents });
      }
      const denominator = Math.max(enteredCenti, covered);
      let assigned = 0;
      let micros = 0;
      parts.forEach((part, i) => {
        const partHours =
          i === parts.length - 1
            ? hoursCenti - assigned
            : divideRoundHalfUp(part.enteredCenti * hoursCenti, denominator);
        assigned += partHours;
        micros += part.costCents * partHours * MICROS_PER_CENTI_CENT;
      });
      return micros;
    };

    // Expansion (RP-4.4) and ramp (RP-4.3) both live in expandLineCells, so the Planned
    // Pod per-Position rollup sees exactly the same cells this does.
    const cells = expandLineCells({
      line,
      months: input.months,
      entryMode: input.entryMode,
      rampMilliByMonth,
    });

    let lineHoursCenti = 0;
    let lineEnteredHoursCenti = 0;
    let lineCostMicros = 0;
    for (const cell of cells) {
      const { enteredCenti, hoursCenti } = cell;
      const thisCellCostMicros = cellCostMicros(cell.month, enteredCenti, hoursCenti);
      lineEnteredHoursCenti += enteredCenti;
      lineHoursCenti += hoursCenti;
      lineCostMicros += thisCellCostMicros;

      // A cell for a month the grid does not show still counts toward the line and the
      // plan. As of the 2026-09-17 product decision, `updateResourcePlan` now PRUNES
      // `plan_line_month` rows outside the new window the moment it moves, so this branch
      // should no longer fire for plans narrowed after that shipped. It stays as the
      // defensive accumulation for legacy plans narrowed before this landed, whose
      // stranded rows nothing has pruned — dropping their hours here would make the
      // totals silently lie, which is the opposite of what this code exists to prevent.
      const idx = monthIndex.get(cell.month);
      if (idx !== undefined) {
        const bucket = byMonthAccum[idx];
        if (bucket) {
          bucket.hoursCenti += hoursCenti;
          bucket.enteredHoursCenti += enteredCenti;
          bucket.revenueMicros += rateCents * hoursCenti * MICROS_PER_CENTI_CENT;
          bucket.costMicros += thisCellCostMicros;
        }
      }
    }

    const lineRevenueMicros = rateCents * lineHoursCenti * MICROS_PER_CENTI_CENT;
    assertSafe(lineRevenueMicros, "line revenueMicros");
    assertSafe(lineCostMicros, "line costMicros");

    // The months the line's cost is judged over: every month the grid shows, plus any
    // stranded cell month (see the note above). With no Resource rate this is one value.
    const pricedMonths = new Set<string>(input.months);
    for (const cell of cells) pricedMonths.add(cell.month);
    // Seeded from the first month, not from the Labor Category cost: a Resource cheaper
    // than their Labor Category across the whole line must lower the bar, not inherit it.
    let maxCostCents: number | null = null;
    let resourceMonths = 0;
    for (const month of pricedMonths) {
      const c = costCentsFor(month);
      maxCostCents = maxCostCents === null ? c : Math.max(maxCostCents, c);
      if (resourceCostCents.has(month) || portionRated.has(month)) resourceMonths += 1;
    }
    maxCostCents ??= costCents;
    const costSource: LineCostSource =
      resourceMonths === 0
        ? "labor_category"
        : resourceMonths === pricedMonths.size
          ? "resource"
          : "mixed";

    // The rate comparison, not the dollar comparison: a line with zero hours is still
    // priced below cost, and the planner should be told before they type the hours.
    // Against the HIGHEST cost in a month the line actually works — so a Resource whose
    // rate rises mid-plan above the bill rate flags the line, but a month still at the
    // Labor Category cost with no hours in it does not (architecture review 2026-10-14).
    // A zero-hour line falls back to every month.
    let workedMaxCostCents: number | null = null;
    for (const cell of cells) {
      if (cell.hoursCenti <= 0) continue;
      const c = workedBarCents(cell.month, cell.enteredCenti);
      workedMaxCostCents = workedMaxCostCents === null ? c : Math.max(workedMaxCostCents, c);
    }
    const belowCost = rateCents < (workedMaxCostCents ?? maxCostCents);
    if (belowCost) belowCostLineCount += 1;

    const costRateCents =
      lineHoursCenti > 0
        ? divideRoundHalfUp(lineCostMicros, lineHoursCenti * MICROS_PER_CENTI_CENT)
        : maxCostCents;

    byLine.push({
      lineId: line.id,
      hoursCenti: lineHoursCenti,
      enteredHoursCenti: lineEnteredHoursCenti,
      revenueMicros: lineRevenueMicros,
      costMicros: lineCostMicros,
      netMicros: lineRevenueMicros - lineCostMicros,
      marginBp: marginAtCents(lineRevenueMicros, lineCostMicros),
      belowCost,
      costSource,
      costRateCents,
    });

    const key = line.workstreamId;
    const ws = wsAccum.get(key) ?? {
      hoursCenti: 0,
      enteredHoursCenti: 0,
      revenueMicros: 0,
      costMicros: 0,
      lineCount: 0,
    };
    ws.hoursCenti += lineHoursCenti;
    ws.enteredHoursCenti += lineEnteredHoursCenti;
    ws.revenueMicros += lineRevenueMicros;
    ws.costMicros += lineCostMicros;
    ws.lineCount += 1;
    wsAccum.set(key, ws);

    planHoursCenti += lineHoursCenti;
    planEnteredHoursCenti += lineEnteredHoursCenti;
    planRevenueMicros += lineRevenueMicros;
    planCostMicros += lineCostMicros;
  }

  assertSafe(planRevenueMicros, "plan revenueMicros");
  assertSafe(planCostMicros, "plan costMicros");

  /**
   * Plan revenue reduced to cents — the operand for every percentage below.
   *
   * The reduction is what keeps a × FULL_PERCENT_BP product inside 2^53 on a large plan;
   * see the header, and marginAtCents, which does exactly the same thing for the same
   * reason. Lossless whenever revenue lands on a whole cent, which it does whenever hours
   * are whole.
   */
  const planRevenueCents = divideRoundHalfUp(planRevenueMicros, MICROS_PER_CENT);

  // ── Discount (RP-1.3) ──
  // Percent and amount are alternatives, enforced by resource_plan_discount_single_form.
  //
  // The percent arm looks as though it has lost its ÷ FULL_PERCENT_BP. It has not:
  // MICROS_PER_CENT and FULL_PERCENT_BP are both 1e4, so `cents × bp` IS
  // `micros × bp ÷ 1e4`, already in Micros. Taking the division as a reduction of the
  // operand rather than of the product is what keeps it inside 2^53 — the old form threw
  // on a 10% discount against a $45M plan, which is a plan RP-4.1 explicitly sanctions.
  const discountMicros =
    input.discountAmount !== null
      ? parseMinor(input.discountAmount, 2) * MICROS_PER_CENT
      : input.discountPercent !== null
        ? planRevenueCents * parseMinor(input.discountPercent, 2)
        : 0;
  assertSafe(discountMicros, "plan discountMicros");

  const feeCents = input.contractedFee === null ? null : parseMinor(input.contractedFee, 2);
  const contractedFeeMicros = feeCents === null ? null : feeCents * MICROS_PER_CENT;
  // RP-D16: a Monthly-basis fee is earned once per month in the window. Measuring it
  // against the whole window's cost read a $16k/month retainer as −650% and sent every
  // multi-month Fixed Price Monthly plan to approval.
  const feeOfRecordCents =
    feeCents === null
      ? null
      : feeOfRecordMinor(feeCents, input.contractedFeeBasis ?? null, input.months.length);
  const contractedFeeOfRecordMicros =
    feeOfRecordCents === null ? null : feeOfRecordCents * MICROS_PER_CENT;
  if (contractedFeeOfRecordMicros !== null) {
    assertSafe(contractedFeeOfRecordMicros, "plan contractedFeeOfRecordMicros");
  }

  // Revenue of record. A discount is a concession on price, so it reduces what we make;
  // a Contracted Fee supersedes the derived figure entirely (§4.4) and is already net of
  // whatever sales conceded, so the discount is not applied twice.
  const revenueOfRecordMicros = contractedFeeOfRecordMicros ?? planRevenueMicros - discountMicros;

  const indicativeMarginBp = marginAtCents(planRevenueMicros, planCostMicros);
  const contractedMarginBp =
    contractedFeeOfRecordMicros === null
      ? null
      : marginAtCents(contractedFeeOfRecordMicros, planCostMicros);

  const marginOfRecordBp = marginAtCents(revenueOfRecordMicros, planCostMicros);
  const marginOfRecordKind: PlanMetrics["marginOfRecordKind"] =
    contractedFeeMicros !== null ? "contracted" : marginOfRecordBp === null ? "none" : "indicative";

  const feeVarianceMicros =
    contractedFeeOfRecordMicros === null ? null : contractedFeeOfRecordMicros - planRevenueMicros;
  // Both operands reduced to cents before the × FULL_PERCENT_BP. At Micros the numerator
  // leaves 2^53 once the variance passes ~$900k, and assertSafe then (correctly) takes the
  // whole plan response down with it. The zero guard moves to the reduced denominator: it
  // still catches zero revenue, and it now also catches a revenue smaller than half a cent
  // rather than dividing by zero.
  const feeVarianceBp =
    feeVarianceMicros === null || planRevenueCents === 0
      ? null
      : divideRoundHalfUp(
          divideRoundHalfUp(feeVarianceMicros, MICROS_PER_CENT) * FULL_PERCENT_BP,
          planRevenueCents,
        );

  const hoursPerFteCenti = input.hoursPerFteMonthCenti;

  // What each month earns on the margin of record — see MonthMetrics.revenueOfRecordMicros.
  const basis = monthRevenueBasis({
    pricingModel: input.pricingModel ?? "TimeAndMaterials",
    contractedFee: input.contractedFee,
    contractedFeeBasis: input.contractedFeeBasis ?? null,
  });
  const spreadCents =
    basis === "fee-spread" && feeCents !== null
      ? spreadByWeights(
          feeCents,
          byMonthAccum.map((m) => m.hoursCenti),
        )
      : null;
  const monthRecord: Array<Micros | null> = byMonthAccum.map((m, i) => {
    if (basis === "monthly-fee") return contractedFeeMicros;
    if (basis === "fee-spread") {
      const cents = spreadCents?.[i];
      return cents === undefined ? null : cents * MICROS_PER_CENT;
    }
    return m.revenueMicros;
  });

  return {
    hoursCenti: planHoursCenti,
    enteredHoursCenti: planEnteredHoursCenti,
    ramped,
    indicativeRevenueMicros: planRevenueMicros,
    costMicros: planCostMicros,
    netMicros: planRevenueMicros - planCostMicros,

    blendedRateMicros: blendedRate(planRevenueMicros, planHoursCenti),

    contractedFeeMicros,
    contractedFeeOfRecordMicros,
    feeVarianceMicros,
    feeVarianceBp,
    discountMicros,

    indicativeMarginBp,
    contractedMarginBp,
    marginOfRecordBp,
    marginOfRecordKind,
    marginStatus:
      marginOfRecordBp === null ? null : marginStatus(marginOfRecordBp, input.marginPolicy),

    byMonth: byMonthAccum.map((m, i) => ({
      month: m.month,
      hoursCenti: m.hoursCenti,
      enteredHoursCenti: m.enteredHoursCenti,
      rampFactor: formatRampMilli(m.rampMilli),
      revenueMicros: m.revenueMicros,
      costMicros: m.costMicros,
      revenueOfRecordMicros: monthRecord[i] ?? null,
      marginBp: marginAtCents(monthRecord[i] ?? 0, m.costMicros),
      fteCenti:
        hoursPerFteCenti === 0 ? 0 : divideRoundHalfUp(m.hoursCenti * 100, hoursPerFteCenti),
    })),

    byWorkstream: [...wsAccum.entries()].map(([workstreamId, ws]) => ({
      workstreamId,
      hoursCenti: ws.hoursCenti,
      enteredHoursCenti: ws.enteredHoursCenti,
      revenueMicros: ws.revenueMicros,
      costMicros: ws.costMicros,
      netMicros: ws.revenueMicros - ws.costMicros,
      marginBp: marginAtCents(ws.revenueMicros, ws.costMicros),
      blendedRateMicros: blendedRate(ws.revenueMicros, ws.hoursCenti),
      lineCount: ws.lineCount,
    })),

    byLine,

    hasBelowCostLine: belowCostLineCount > 0,
    belowCostLineCount,
  };
}

/**
 * Blended Rate — Total Revenue ÷ Total Hours, in Micros.
 *
 * Derived from the UNREDUCED Micros revenue, because this one IS a per-hour average and
 * its sub-cent tail is exactly what the M1 rounding note is about. Never an average of
 * the rate column (RP-6.4).
 */
function blendedRate(revenueMicros: Micros, hoursCenti: HoursCenti): Micros | null {
  if (hoursCenti === 0) return null;
  return divideRoundHalfUp(revenueMicros * 100, hoursCenti);
}

/**
 * Margin in bp, computed on the pair reduced to cents.
 *
 * The reduction is what keeps `× FULL_PERCENT_BP` inside 2^53 on a large plan — see the
 * header. It is lossless whenever hours are whole.
 */
function marginAtCents(revenueMicros: Micros, costMicros: Micros): Bp | null {
  if (revenueMicros === 0) return null;
  return marginBp(
    divideRoundHalfUp(revenueMicros, MICROS_PER_CENT),
    divideRoundHalfUp(costMicros, MICROS_PER_CENT),
  );
}

// ── Estimate reconciliation ───────────────────────────────────────────────────

/**
 * How far the grid has drifted from what the work was sized at.
 *
 * Bands: within ±5% reconciled, within ±10% warn, beyond that red. The estimate never
 * drives the plan — it is the number the plan is CHECKED against. Workstream grain
 * deliberately: per-deliverable hours belong in the Estimate system (§11).
 */
export type ReconciliationBand = "reconciled" | "warn" | "red" | "no-estimate";

const RECONCILED_BAND_BP = 500;
const WARN_BAND_BP = 1_000;

export interface Reconciliation {
  estimatedHoursCenti: HoursCenti | null;
  plannedHoursCenti: HoursCenti;
  /** planned − estimated. Signed. Null with no estimate. */
  varianceHoursCenti: HoursCenti | null;
  /** (planned − estimated) ÷ estimated, in bp. Signed. Null with no estimate. */
  varianceBp: Bp | null;
  band: ReconciliationBand;
}

export function reconcileEstimate(input: {
  estimatedHours: string | null;
  plannedHoursCenti: HoursCenti;
}): Reconciliation {
  if (input.estimatedHours === null) {
    return {
      estimatedHoursCenti: null,
      plannedHoursCenti: input.plannedHoursCenti,
      varianceHoursCenti: null,
      varianceBp: null,
      band: "no-estimate",
    };
  }

  const estimatedHoursCenti = parseHoursCenti(input.estimatedHours);
  const varianceHoursCenti = input.plannedHoursCenti - estimatedHoursCenti;

  // An estimate of zero has no percentage to be off by. Treat it as "no estimate" for
  // banding rather than dividing by zero or claiming infinite drift.
  if (estimatedHoursCenti === 0) {
    return {
      estimatedHoursCenti,
      plannedHoursCenti: input.plannedHoursCenti,
      varianceHoursCenti,
      varianceBp: null,
      band: "no-estimate",
    };
  }

  const varianceBp = divideRoundHalfUp(varianceHoursCenti * FULL_PERCENT_BP, estimatedHoursCenti);
  const magnitude = Math.abs(varianceBp);

  return {
    estimatedHoursCenti,
    plannedHoursCenti: input.plannedHoursCenti,
    varianceHoursCenti,
    varianceBp,
    band:
      magnitude <= RECONCILED_BAND_BP ? "reconciled" : magnitude <= WARN_BAND_BP ? "warn" : "red",
  };
}
